import { z } from "zod";

import { coarseLocationSchema } from "../../features/location/coarseLocation";
import {
  dedupeRemoteDrivers,
  excludeLocalDriver,
} from "../../features/session/remoteDrivers";
import {
  LANES,
  REACTIONS,
  VEHICLE_PRESETS,
  type Lane,
  type RemoteDriver,
  type TrafficEvent,
} from "../../types/traffic";

const presenceStateSchema = z.object({
  sessionId: z.string().min(8).max(80),
  publicDriverId: z.string().min(8).max(80),
  lane: z.coerce
    .number()
    .int()
    .refine((value): value is Lane =>
      (LANES as readonly number[]).includes(value),
    ),
  joinedAt: z.iso.datetime(),
  vehiclePreset: z.enum(VEHICLE_PRESETS),
  colorId: z.string().regex(/^[a-z-]{3,24}$/),
  plate: z.string().regex(/^[A-Z0-9](?:[A-Z0-9 -]{0,6}[A-Z0-9])?$/),
  badge: z
    .enum(["garage", "founding"])
    .nullish()
    .transform((value) => value ?? null),
  sessionElapsedSeconds: z.coerce.number().min(0).max(86_400),
  sessionDistanceMiles: z.coerce.number().min(0).max(1_000_000),
  location: coarseLocationSchema.nullable().catch(null),
}) satisfies z.ZodType<RemoteDriver>;

const trafficEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("honk"),
    sessionId: z.string().min(8).max(80),
    at: z.number().int().nonnegative(),
  }),
  z.object({
    type: z.literal("flash"),
    sessionId: z.string().min(8).max(80),
    at: z.number().int().nonnegative(),
  }),
  z.object({
    type: z.literal("reaction"),
    sessionId: z.string().min(8).max(80),
    reaction: z.enum(REACTIONS),
    at: z.number().int().nonnegative(),
  }),
  z.object({
    type: z.literal("lane"),
    sessionId: z.string().min(8).max(80),
    lane: z.coerce
      .number()
      .int()
      .refine((value): value is Lane =>
        (LANES as readonly number[]).includes(value),
      ),
    at: z.number().int().nonnegative(),
  }),
  z.object({
    type: z.literal("exit"),
    sessionId: z.string().min(8).max(80),
    at: z.number().int().nonnegative(),
  }),
]) satisfies z.ZodType<TrafficEvent>;

function normalizePresencePayload(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  const metas = record.metas;
  if (!Array.isArray(metas) || metas.length === 0) return value;
  const [meta] = metas as unknown[];
  if (!meta || typeof meta !== "object") return value;
  return { ...record, ...meta };
}

export function parsePresenceState(value: unknown): RemoteDriver | null {
  const normalized = normalizePresencePayload(value);
  const result = presenceStateSchema.safeParse(normalized);
  if (result.success) {
    return {
      ...result.data,
      location: result.data.location ?? null,
    };
  }
  return parsePresenceStateLoose(normalized);
}

function asTrimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function parsePresenceStateLoose(value: unknown): RemoteDriver | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const sessionId = asTrimmedString(record.sessionId);
  const publicDriverId = asTrimmedString(record.publicDriverId);
  if (sessionId.length < 8 || publicDriverId.length < 8) return null;
  const lane = Number(record.lane);
  if (!(LANES as readonly number[]).includes(lane)) return null;
  const plate = asTrimmedString(record.plate)
    .toUpperCase()
    .replace(/[^A-Z0-9 -]/g, "")
    .slice(0, 8);
  if (!plate) return null;
  const vehiclePreset = VEHICLE_PRESETS.includes(
    record.vehiclePreset as (typeof VEHICLE_PRESETS)[number],
  )
    ? (record.vehiclePreset as (typeof VEHICLE_PRESETS)[number])
    : "sedan";
  const colorId = /^[a-z-]{3,24}$/.test(asTrimmedString(record.colorId))
    ? asTrimmedString(record.colorId)
    : "jam-orange";
  const joinedAt =
    typeof record.joinedAt === "string" &&
    !Number.isNaN(Date.parse(record.joinedAt))
      ? new Date(record.joinedAt).toISOString()
      : new Date().toISOString();
  const elapsed = Number(record.sessionElapsedSeconds);
  const distance = Number(record.sessionDistanceMiles);
  return {
    sessionId: sessionId.slice(0, 80),
    publicDriverId: publicDriverId.slice(0, 80),
    lane: lane as Lane,
    joinedAt,
    vehiclePreset,
    colorId,
    plate,
    badge:
      record.badge === "garage" || record.badge === "founding"
        ? record.badge
        : null,
    sessionElapsedSeconds: Number.isFinite(elapsed)
      ? Math.max(0, Math.min(86_400, elapsed))
      : 0,
    sessionDistanceMiles: Number.isFinite(distance)
      ? Math.max(0, Math.min(1_000_000, distance))
      : 0,
    location: coarseLocationSchema
      .nullable()
      .catch(null)
      .parse(record.location),
  };
}

export function collectPresenceDrivers(
  state: unknown,
  localSessionId?: string | null,
  localPublicDriverId?: string | null,
): RemoteDriver[] {
  if (!state || typeof state !== "object") return [];
  const drivers: RemoteDriver[] = [];
  const seen = new Set<string>();
  for (const value of Object.values(state as Record<string, unknown>)) {
    const candidates = Array.isArray(value)
      ? value
      : value &&
          typeof value === "object" &&
          Array.isArray((value as { metas?: unknown }).metas)
        ? (value as { metas: unknown[] }).metas
        : value
          ? [value]
          : [];
    for (const candidate of candidates) {
      const driver = parsePresenceState(candidate);
      if (
        !driver ||
        driver.sessionId === localSessionId ||
        seen.has(driver.sessionId)
      ) {
        continue;
      }
      seen.add(driver.sessionId);
      drivers.push(driver);
    }
  }
  return dedupeRemoteDrivers(
    excludeLocalDriver(drivers, localSessionId, localPublicDriverId),
  );
}

export function parseTrafficEvent(value: unknown): TrafficEvent | null {
  const result = trafficEventSchema.safeParse(value);
  return result.success ? result.data : null;
}

export function isEventFromKnownRemote(
  event: TrafficEvent,
  remoteSessionIds: ReadonlySet<string>,
): boolean {
  return remoteSessionIds.has(event.sessionId);
}

export function canPublishTrafficEvent(
  event: TrafficEvent,
  localSessionId: string | null | undefined,
  subscribed: boolean,
): boolean {
  return (
    subscribed && Boolean(localSessionId) && event.sessionId === localSessionId
  );
}

export type CohortConnectionInput = {
  sessionId: string;
  channel: string;
  driver: RemoteDriver;
};

export type RealtimeUpdate =
  | { type: "snapshot"; drivers: RemoteDriver[] }
  | { type: "join"; driver: RemoteDriver }
  | { type: "leave"; sessionId: string }
  | { type: "event"; event: TrafficEvent }
  | {
      type: "connection";
      state: "connected" | "connecting" | "offline";
    };

export type RealtimeListener = (update: RealtimeUpdate) => void;

export type TrafficRealtimeProvider = {
  connect(input: CohortConnectionInput): Promise<void>;
  reconnect(input: CohortConnectionInput): Promise<void>;
  updateLane(lane: Lane): Promise<void>;
  updatePresence(driver: RemoteDriver): Promise<void>;
  publish(event: TrafficEvent): Promise<void>;
  disconnect(): Promise<void>;
  isConnected(): boolean;
  subscribe(listener: RealtimeListener): () => void;
};
